"use client"

import { useState, useEffect, useCallback } from "react"
import { Palette, ChevronLeft, Check } from "lucide-react"
import { cn } from "../../../lib/utils"

// ─── Types ────────────────────────────────────────────────────────────────────

interface ColorBlock {
  part: string
  label: string
  colors: string[]
}

const PART_LABELS: Record<string, string> = {
  gola:   "Gola",
  camisa: "Camisa",
  manga:  "Manga",
  short:  "Calção",
  meia:   "Meia",
  outros: "Outros",
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function readWindowColors(): ColorBlock[] {
  const w = window as any
  const parts = [
    { key: "gola",   winKey: "uniCorGolaArray"     },
    { key: "camisa", winKey: "uniCorCamisaArray"    },
    { key: "manga",  winKey: "uniCorMangaArray"     },
    { key: "short",  winKey: "uniCorcorShortArray"  },
    { key: "meia",   winKey: "uniCorcorMeiaArray"   },
    { key: "outros", winKey: "uniCorcorOutrosArray" },
  ]
  const blocks: ColorBlock[] = []
  for (const { key, winKey } of parts) {
    const colors: string[] = w[winKey] ?? []
    if (colors.length > 0)
      blocks.push({ part: key, label: PART_LABELS[key], colors: [...colors] })
  }
  return blocks
}

/** Resolve the preset color table for a given part from window.dataShared + window.colors */
function resolveTableColors(part: string): string[] {
  const w = window as any
  const allTables: any[] = w.colors ?? []
  const dataShared = w.dataShared

  if (!allTables.length) return []

  // 1. Try part-specific table
  const partKey = part === "short" ? "calcao" : part
  const partTableId = dataShared?.colors?.[partKey]
  if (partTableId) {
    const table = allTables.find(t => t.id === partTableId)
    if (table?.colors?.length) return table.colors.map((c: any) => c.hex)
  }

  // 2. Fallback to mainColorTable
  const mainId = dataShared?.mainColorTable
  if (mainId) {
    const table = allTables.find(t => t.id === mainId)
    if (table?.colors?.length) return table.colors.map((c: any) => c.hex)
  }

  // 3. Fallback: first available table
  if (allTables[0]?.colors?.length) return allTables[0].colors.map((c: any) => c.hex)

  return []
}

function toHex(color: string): string {
  if (color.startsWith("#")) return color
  const m = color.match(/\d+/g)
  if (m && m.length >= 3)
    return "#" + [m[0], m[1], m[2]].map(n => Number(n).toString(16).padStart(2, "0")).join("")
  return "#000000"
}

function applyColorChange(part: string, fromColor: string, toColor: string) {
  const w = window as any
  const arrayMap: Record<string, string> = {
    camisa: "corCamisaArray", gola: "corGolaArray", manga: "corMangaArray",
    short: "corShortArray",   meia: "corMeiaArray", outros: "corOutrosArray",
  }
  const arr: any[] = w[arrayMap[part]] ?? []
  arr.forEach(obj => {
    if (obj.fillIni === fromColor) {
      obj.set({ fill: toColor })
      obj.fillIni = toColor
    }
  })
  w.fabricCanvas?.renderAll()
  w["updateCanvas"]?.()
}

// ─── Swatch ───────────────────────────────────────────────────────────────────

function Swatch({ color, size = "md", selected = false, onClick }: {
  color: string; size?: "sm" | "md" | "lg"; selected?: boolean; onClick?: () => void
}) {
  const dim = size === "lg" ? "w-9 h-9" : size === "sm" ? "w-5 h-5" : "w-7 h-7"
  const hex = toHex(color)
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  const isLight = (r * 0.299 + g * 0.587 + b * 0.114) > 186

  return (
    <button
      onClick={onClick}
      title={color}
      className={cn(
        "rounded-xl border-2 transition-all duration-150 active:scale-95 flex items-center justify-center flex-shrink-0 touch-manipulation",
        dim,
        selected
          ? "border-primary ring-2 ring-primary/40 scale-110 shadow-lg"
          : isLight ? "border-border/60 hover:border-primary/60" : "border-white/10 hover:border-white/40"
      )}
      style={{ backgroundColor: color }}
    >
      {selected && (
        <Check className={cn("w-3.5 h-3.5", isLight ? "text-gray-800" : "text-white")} strokeWidth={3} />
      )}
    </button>
  )
}

// ─── Color block row ──────────────────────────────────────────────────────────
// Shows all colors of a garment part. Each color swatch is a clickable target
// that opens the edit panel pre-focused on that specific color slot.

function ColorBlockRow({ block, onSelect }: {
  block: ColorBlock
  onSelect: (part: string, color: string) => void
}) {
  return (
    <div className="rounded-2xl bg-secondary/10 border border-border/30 overflow-hidden">
      {/* Part label bar */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border/20 bg-secondary/20">
        <span className="text-xs font-semibold text-foreground/80 uppercase tracking-wider">
          {block.label}
        </span>
        <span className="text-[10px] text-muted-foreground/50 ml-auto">
          {block.colors.length} {block.colors.length === 1 ? "cor" : "cores"}
        </span>
      </div>

      {/* Color swatches — click any to edit that specific color slot */}
      <div className="p-2.5 flex gap-2 flex-wrap">
        {block.colors.map(color => (
          <button
            key={`${block.part}-${color}`}
            onClick={() => onSelect(block.part, color)}
            title={`Editar ${color}`}
            className="group relative flex flex-col items-center gap-1 touch-manipulation"
          >
            <div
              className="w-10 h-10 rounded-xl border-2 border-white/10 group-hover:border-primary/60 group-hover:scale-105 group-active:scale-95 transition-all duration-150 shadow-sm"
              style={{ backgroundColor: color }}
            />
            {/* pencil overlay on hover */}
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              <div className="w-4 h-4 rounded-full bg-black/40 flex items-center justify-center">
                <svg viewBox="0 0 12 12" className="w-2.5 h-2.5 fill-white">
                  <path d="M8.5 1.5a1.5 1.5 0 0 1 2.12 2.12L9.5 4.74 7.26 2.5 8.5 1.5ZM6.5 3.26 1 8.76V11h2.24l5.5-5.5L6.5 3.26Z"/>
                </svg>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  )
}

// ─── Edit panel ───────────────────────────────────────────────────────────────
// Shows ALL color slots of the part so user can switch between them,
// plus the full color table to pick a new color for the selected slot.

function ColorEditPanel({ part, partColors, activeColor, onBack, onApply, onSwitchSlot }: {
  part: string
  partColors: string[]        // all current colors of this part
  activeColor: string         // the slot being edited right now
  onBack: () => void
  onApply: (newColor: string) => void
  onSwitchSlot: (color: string) => void
}) {
  const [tableColors, setTableColors] = useState<string[]>([])

  useEffect(() => {
    const load = () => setTableColors(resolveTableColors(part))
    load()
    window.addEventListener("svgColorsReady", load)
    return () => window.removeEventListener("svgColorsReady", load)
  }, [part])

  const activeHex = toHex(activeColor)

  return (
    <div className="space-y-4 animate-in slide-in-from-right-4 duration-200">

      {/* Header: back + part name + current active color */}
      <div className="flex items-center gap-2">
        <button onClick={onBack}
          className="w-8 h-8 rounded-xl bg-secondary/60 hover:bg-secondary active:scale-95 flex items-center justify-center transition-all touch-manipulation flex-shrink-0">
          <ChevronLeft className="w-4 h-4 text-muted-foreground" />
        </button>
        <span className="text-sm font-semibold text-foreground">{PART_LABELS[part] ?? part}</span>
      </div>

      {/* Part color slots — tap to switch which slot you're editing */}
      {partColors.length > 1 && (
        <div className="space-y-1.5">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            Selecionar cor para editar
          </p>
          <div className="flex gap-2 flex-wrap rounded-2xl bg-secondary/20 p-2.5">
            {partColors.map(color => {
              const isActive = color === activeColor
              return (
                <button
                  key={color}
                  onClick={() => onSwitchSlot(color)}
                  title={color}
                  className={cn(
                    "relative w-10 h-10 rounded-xl border-2 transition-all duration-150 touch-manipulation flex-shrink-0",
                    isActive
                      ? "border-primary ring-2 ring-primary/40 scale-110 shadow-lg"
                      : "border-white/10 hover:border-primary/50 hover:scale-105 active:scale-95"
                  )}
                  style={{ backgroundColor: color }}
                >
                  {isActive && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-3 h-3 rounded-full bg-primary/80 flex items-center justify-center">
                        <svg viewBox="0 0 12 12" className="w-2 h-2 fill-white">
                          <path d="M8.5 1.5a1.5 1.5 0 0 1 2.12 2.12L9.5 4.74 7.26 2.5 8.5 1.5ZM6.5 3.26 1 8.76V11h2.24l5.5-5.5L6.5 3.26Z"/>
                        </svg>
                      </div>
                    </div>
                  )}
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/* Currently editing indicator */}
      <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-secondary/30 border border-border/30">
        <span className="text-xs text-muted-foreground">Editando:</span>
        <div className="w-5 h-5 rounded-md border border-border flex-shrink-0" style={{ backgroundColor: activeColor }} />
        <span className="text-xs font-mono text-muted-foreground">{activeHex}</span>
      </div>

      {/* Color table to pick from */}
      {tableColors.length > 0 ? (
        <div className="space-y-1.5">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            Cores disponíveis
            <span className="normal-case font-normal ml-1 text-muted-foreground/50">({tableColors.length})</span>
          </p>
          <div className="rounded-2xl bg-secondary/20 p-2.5 max-h-64 overflow-y-auto">
            <div className="flex flex-wrap gap-1.5">
              {tableColors.map(color => (
                <Swatch
                  key={color}
                  color={color}
                  size="lg"
                  selected={activeColor === color}
                  onClick={() => onApply(color)}
                />
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-2xl bg-secondary/20 p-4 text-center text-xs text-muted-foreground">
          Tabela de cores não configurada para este projeto
        </div>
      )}
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

export function ColorPicker() {
  const [blocks, setBlocks] = useState<ColorBlock[]>([])
  // editing: which part + which specific color slot is active
  const [editing, setEditing] = useState<{ part: string; activeColor: string } | null>(null)

  const refreshColors = useCallback(() => setBlocks(readWindowColors()), [])

  useEffect(() => {
    refreshColors()
    window.addEventListener("svgColorsReady", refreshColors)
    return () => window.removeEventListener("svgColorsReady", refreshColors)
  }, [refreshColors])

  // Apply color to the active slot and update state
  const handleApply = (newColor: string) => {
    if (!editing) return
    applyColorChange(editing.part, editing.activeColor, newColor)

    // Update the swatch in the list
    setBlocks(prev =>
      prev.map(b =>
        b.part === editing.part
          ? { ...b, colors: b.colors.map(c => (c === editing.activeColor ? newColor : c)) }
          : b
      )
    )
    // Keep editing the same slot with updated color
    setEditing(e => (e ? { ...e, activeColor: newColor } : null))
  }

  // Switch which color slot within the same part is being edited
  const handleSwitchSlot = (color: string) => {
    setEditing(e => (e ? { ...e, activeColor: color } : null))
  }

  // The full list of colors for the part currently being edited
  const editingBlock = editing
    ? blocks.find(b => b.part === editing.part)
    : null

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-primary/10 rounded-lg flex-shrink-0">
          <Palette className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h3 className="font-semibold text-foreground">Cores</h3>
          <p className="text-xs text-muted-foreground">
            {blocks.length > 0 ? "Clique em uma cor para editar" : "Carregando cores do uniforme…"}
          </p>
        </div>
      </div>

      {editing && editingBlock ? (
        <ColorEditPanel
          part={editing.part}
          partColors={editingBlock.colors}
          activeColor={editing.activeColor}
          onBack={() => setEditing(null)}
          onApply={handleApply}
          onSwitchSlot={handleSwitchSlot}
        />
      ) : blocks.length > 0 ? (
        <div className="space-y-3">
          {blocks.map(block => (
            <ColorBlockRow
              key={block.part}
              block={block}
              onSelect={(part, color) => setEditing({ part, activeColor: color })}
            />
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-10 gap-3 text-center">
          <div className="w-12 h-12 rounded-2xl bg-secondary/40 flex items-center justify-center">
            <Palette className="w-6 h-6 text-muted-foreground animate-pulse" />
          </div>
          <p className="text-sm text-muted-foreground max-w-[200px]">
            Carregue um modelo para ver as cores disponíveis
          </p>
        </div>
      )}
    </div>
  )
}